import { isTrackingOptedOut, type ConsentAction, type EventKind } from "./analytics";


const VISITOR_KEY = "sellavo_visitor";
const SESSION_KEY = "sellavo_session";
const SEEN_KEY = "sellavo_seen_before";

function randomId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Långlivat ID sparas bara om analyssamtycke finns – annars räcker sessionen. */
function ids(): { visitorId: string; sessionId: string; isNew: boolean } {
  let visitorId = "";
  let isNew = false;
  try {
    const consentRaw =
      localStorage.getItem("sellavo-cookie-consent") ??
      localStorage.getItem("saljavo-cookie-consent");
    const analyticsOk = consentRaw ? Boolean(JSON.parse(consentRaw)?.analytics) : false;
    if (analyticsOk) {
      visitorId = localStorage.getItem(VISITOR_KEY) ?? "";
      if (!visitorId) {
        visitorId = randomId();
        localStorage.setItem(VISITOR_KEY, visitorId);
      }
      isNew = !localStorage.getItem(SEEN_KEY);
      if (isNew) localStorage.setItem(SEEN_KEY, new Date().toISOString());
    }
  } catch {
    /* blockerad lagring – kör sessionsbaserat */
  }

  let sessionId = "";
  try {
    sessionId = sessionStorage.getItem(SESSION_KEY) ?? "";
    if (!sessionId) {
      sessionId = randomId();
      sessionStorage.setItem(SESSION_KEY, sessionId);
      if (!visitorId) isNew = true;
    }
  } catch {
    sessionId = randomId();
  }

  if (!visitorId) visitorId = `s:${sessionId}`;
  return { visitorId, sessionId, isNew };
}

function deviceInfo() {
  const ua = navigator.userAgent;
  const device = /iPad|Tablet/i.test(ua)
    ? "tablet"
    : /Mobi|Android|iPhone/i.test(ua)
      ? "mobil"
      : "dator";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /OPR\//.test(ua)
      ? "Opera"
      : /Chrome\//.test(ua)
        ? "Chrome"
        : /Firefox\//.test(ua)
          ? "Firefox"
          : /Safari\//.test(ua)
            ? "Safari"
            : "Övrig";
  const os = /Windows/.test(ua)
    ? "Windows"
    : /Android/.test(ua)
      ? "Android"
      : /iPhone|iPad|iOS/.test(ua)
        ? "iOS"
        : /Mac OS X/.test(ua)
          ? "macOS"
          : /Linux/.test(ua)
            ? "Linux"
            : "Övrigt";
  return { device, browser, os };
}

/** True när sidan körs som installerad app (hemskärm) i stället för webbläsare. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const mm = ["standalone", "fullscreen", "minimal-ui"].some(
      (m) => window.matchMedia?.(`(display-mode: ${m})`).matches,
    );
    const ios = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    const android = document.referrer.startsWith("android-app://");
    return mm || ios || android;
  } catch {
    return false;
  }
}

/**
 * Extra fält när sidan körs i den nativa app-wrappern. Vi läser window direkt
 * (i stället för app-wrapper-modulen) för att undvika cirkulära importer.
 */
function appModeMeta(): Record<string, string | number | boolean> {
  if (typeof window === "undefined") return {};
  const api = (window as unknown as { AppWrapper?: unknown }).AppWrapper;
  if (!api || typeof api !== "object") return {};
  const info = (window as unknown as { __appWrapperInfo?: Record<string, string> })
    .__appWrapperInfo;
  return {
    appMode: true,
    appVersion: info?.appVersion ?? "",
    deviceModel: info?.model ?? "",
    osVersion: info?.system ?? "",
  };
}

let authState = { isAuthenticated: false, plan: "anon", isAdmin: false };

/** Uppdateras av appen när inloggning, kontotyp eller adminroll ändras. */
export function setAnalyticsIdentity(isAuthenticated: boolean, plan: string, isAdmin = false) {
  authState = {
    isAuthenticated,
    plan: plan || (isAuthenticated ? "free" : "anon"),
    isAdmin,
  };
}

export function currentSessionId(): string {
  return ids().sessionId;
}

/** Skickar en händelse. Fel ignoreras – statistik får aldrig störa användaren. */
export function track(
  kind: EventKind,
  extra: {
    path?: string;
    adSlug?: string | null;
    title?: string;
    label?: string;
    value?: number;
    meta?: Record<string, string | number | boolean>;
    keepalive?: boolean;
  } = {},
) {
  if (typeof window === "undefined") return;
  if (isTrackingOptedOut()) return;
  const { visitorId, sessionId, isNew } = ids();
  const info = deviceInfo();
  // Serveranropet laddas som eget paket så startsidan slipper det.
  void import("./analytics.functions").then((m) =>
    m.trackSiteEvent({
    data: {
      kind,
      path: extra.path ?? window.location.pathname + window.location.search,
      adSlug: extra.adSlug ?? null,
      title: (extra.title ?? document.title ?? "").slice(0, 200),
      label: extra.label ?? "",
      value: typeof extra.value === "number" ? Math.round(extra.value) : null,
      sessionId,
      visitorId,
      referrer: document.referrer || "",
      device: info.device,
      browser: info.browser,
      os: info.os,
      lang: navigator.language || "",
      isAuthenticated: authState.isAuthenticated,
      plan: authState.plan,
      isAdmin: authState.isAdmin,
      isNewVisitor: isNew,
      isPwa: isStandalone(),
      meta: { ...appModeMeta(), ...(extra.meta ?? {}) },
    },
    }),
  ).catch(() => {});
}

/** Klick på en viktig knapp, t.ex. "Börja här" eller "Kontakta säljare". */
export const trackClick = (label: string, meta?: Record<string, string | number | boolean>) =>
  track("click", { label, meta });

/** Hur långt ner besökaren läste på en sida (procent). */
export const trackScroll = (path: string, depth: number) =>
  track("scroll", { path, value: depth });

/** Hela förloppet i cookie-rutan – visad, öppnad, svarad eller ändrad. */
export const trackConsent = (
  action: ConsentAction,
  meta: Record<string, string | number | boolean> = {},
) => track("consent", { label: action, meta });

